/**
 * <s-recently-viewed>: remembers the product on the current page and lists the
 * other recently viewed products. Data is stored at view time in localStorage
 * (`velle:recent`), so the list renders without network requests.
 *
 * Attributes on the host: data-product-id, data-product-title, data-product-url,
 * data-product-image, data-product-price. Children: [data-recent-list] (target),
 * [data-recent-empty] (shown when nothing else was viewed), <template data-recent-row>.
 */
const STORAGE_KEY = 'velle:recent';
const MAX_ITEMS = 12;

function readItems() {
  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    const current = {
      id: this.dataset.productId,
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      image: this.dataset.productImage,
      price: this.dataset.productPrice,
    };
    const previous = readItems().filter((item) => item && item.id && item.id !== current.id);
    this.#render(previous);
    if (current.id) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([current, ...previous].slice(0, MAX_ITEMS)));
      } catch {
        /* storage full or blocked: the list simply stays empty */
      }
    }
  }

  #render(items) {
    const list = this.querySelector('[data-recent-list]');
    const empty = this.querySelector('[data-recent-empty]');
    const template = this.querySelector('template[data-recent-row]');
    if (!list || !template) return;
    list.replaceChildren();
    for (const item of items) {
      const row = template.content.firstElementChild.cloneNode(true);
      row.querySelectorAll('[data-recent-link]').forEach((link) => link.setAttribute('href', item.url || '#'));
      const img = row.querySelector('[data-recent-image]');
      if (img) {
        if (item.image) {
          img.src = item.image;
          img.alt = item.title || '';
        } else {
          img.remove();
        }
      }
      const title = row.querySelector('[data-recent-title]');
      if (title) title.textContent = item.title || '';
      const price = row.querySelector('[data-recent-price]');
      if (price) price.textContent = item.price || '';
      list.append(row);
    }
    if (empty) empty.hidden = items.length > 0;
    list.hidden = items.length === 0;
  }
}

if (!customElements.get('s-recently-viewed')) {
  customElements.define('s-recently-viewed', RecentlyViewed);
}
